import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';

/* ── Icônes ── */
const IcoUpload  = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>;
const IcoFile    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const IcoCheck   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IcoX       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoDown    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IcoUsers   = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoCal     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;

const s = {
  card:    { background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--border)', boxShadow: 'var(--card-shadow)', padding: '24px', marginBottom: 16 },
  label:   { display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 },
  sublabel:{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 },
};

/* ── Modèle CSV téléchargeable ── */
const TEMPLATES = {
  activites: {
    headers: ['Evenement', 'Date', 'Heure Début', 'Heure Fin', 'Description'],
    example:  [['Culte du dimanche', '2026-06-15', '09:30', '12:00', 'Grand culte hebdomadaire']],
  },
  participants: {
    headers: ['Prenom', 'Nom', 'Genre', 'Statut', 'Telephone', 'Email', 'Ville', 'Famille'],
    example:  [['Alice', 'Fofana', 'femme', 'membre', '+225 01 02 03 04', 'alice@email.com', 'Abidjan', 'Grâce']],
  },
};

function downloadTemplate(type) {
  const t = TEMPLATES[type];
  const csv = [t.headers, ...t.example].map(r => r.join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `modele_${type}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/* ── Zone de drop ── */
function DropZone({ onFile, accept = '.xlsx,.xls,.csv', disabled }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);

  const handle = (f) => {
    if (!f) return;
    setFile(f);
    onFile(f);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); if (!disabled) handle(e.dataTransfer.files[0]); }}
      onClick={() => !disabled && ref.current?.click()}
      style={{
        border: `2px dashed ${dragging ? 'var(--primary)' : 'var(--border)'}`,
        borderRadius: 14,
        padding: '32px 20px',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: dragging ? 'var(--page-bg)' : 'transparent',
        transition: 'all .2s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => handle(e.target.files?.[0])} />
      <div style={{ color: 'var(--primary)', marginBottom: 10 }}><IcoUpload /></div>
      {file ? (
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--page-bg)', borderRadius: 8, padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            <IcoFile /> {file.name}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
            {(file.size / 1024).toFixed(1)} Ko · Cliquer pour changer
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
            Déposez votre fichier ici
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Excel (.xlsx, .xls) ou CSV — ou cliquez pour choisir
          </div>
        </>
      )}
    </div>
  );
}

/* ── Résultat d'import ── */
function ImportResult({ result, type }) {
  if (!result) return null;
  const success = result.created > 0;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${success ? '#bbf7d0' : '#fecaca'}`, marginTop: 16 }}>
      <div style={{ background: success ? '#f0fdf4' : '#fef2f2', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: success ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
          {success ? <IcoCheck /> : <IcoX />}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: success ? '#15803d' : '#b91c1c' }}>{result.message}</div>
          <div style={{ fontSize: '0.75rem', color: success ? '#16a34a' : '#dc2626', marginTop: 2 }}>
            {result.created} importé(s) · {result.skipped} ignoré(s)
          </div>
        </div>
      </div>
      {result.errors?.length > 0 && (
        <div style={{ padding: '12px 16px', background: '#fff' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Erreurs ({result.errors.length})
          </div>
          {result.errors.map((e, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: '#dc2626', padding: '4px 0', borderBottom: '1px solid #fef2f2' }}>{e}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Section d'import ── */
function ImportSection({ title, subtitle, icon, type, endpoint, churchId, isPlatformAdmin, accentColor }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!file) { toast.error('Veuillez sélectionner un fichier.'); return; }
    if (isPlatformAdmin && !churchId) { toast.error('Veuillez sélectionner une église.'); return; }

    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (res.data.created > 0) toast.success(res.data.message);
      else toast.error('Aucune ligne importée. Vérifiez le format du fichier.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'import.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: `${accentColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor }}>
            {icon}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)' }}>{title}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>
          </div>
        </div>
        {/* Télécharger modèle */}
        <button onClick={() => downloadTemplate(type)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--page-bg)', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.color = accentColor; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
          <IcoDown /> Modèle CSV
        </button>
      </div>

      {/* Colonnes attendues */}
      <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--page-bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Colonnes reconnues</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {TEMPLATES[type].headers.map(h => (
            <span key={h} style={{ fontSize: '0.72rem', padding: '2px 8px', background: `${accentColor}12`, color: accentColor, borderRadius: 6, fontWeight: 600 }}>{h}</span>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      <DropZone onFile={setFile} disabled={loading} />

      {/* Bouton import */}
      <button
        onClick={handleImport}
        disabled={!file || loading}
        style={{ width: '100%', marginTop: 14, padding: '13px', background: file && !loading ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` : '#cbd5e1', color: file && !loading ? 'white' : '#64748b', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: '0.95rem', cursor: file && !loading ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all .2s' }}>
        {loading ? (
          <>
            <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
            Import en cours…
          </>
        ) : (
          <><IcoUpload /> Importer {title.toLowerCase()}</>
        )}
      </button>

      {/* Résultat */}
      <ImportResult result={result} type={type} />
    </div>
  );
}

/* ════════════════════════════
   PAGE PRINCIPALE
════════════════════════════ */
export default function Import() {
  const { user } = useAuth();
  const { activeChurchId, churches, selectedChurch } = useChurch();
  const isPlatformAdmin = user?.role === 'superadmin_platform';

  const churchId = isPlatformAdmin ? activeChurchId : (user?.church_id);
  const churchName = isPlatformAdmin
    ? (selectedChurch?.name || 'Vue globale')
    : (user?.church_name || 'votre église');

  return (
    <div style={{ width: '100%' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Import de données
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 4, marginBottom: 0 }}>
          Importez vos activités et fidèles depuis un fichier Excel ou CSV.
        </p>
      </div>

      {/* Bandeau église active */}
      <div style={{ marginBottom: 20, padding: '12px 16px', background: 'var(--page-bg)', borderRadius: 12, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)', boxShadow: '0 0 6px var(--primary)' }}/>
        <div>
          <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.88rem' }}>
            Église cible : {churchName}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginLeft: 8 }}>
            Les données importées seront associées à cette église uniquement.
          </span>
        </div>
        {isPlatformAdmin && !activeChurchId && (
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, background: '#fef2f2', padding: '3px 10px', borderRadius: 8, border: '1px solid #fecaca' }}>
            ⚠ Sélectionnez une église dans la navbar
          </span>
        )}
      </div>

      {/* Sections d'import */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 0 }}>
        <ImportSection
          title="Activités"
          subtitle="Importez vos événements, cultes et réunions"
          icon={<IcoCal />}
          type="activites"
          endpoint="/notifications/import/activites/"
          churchId={churchId}
          isPlatformAdmin={isPlatformAdmin}
          accentColor="var(--primary)"
        />
        <ImportSection
          title="Fidèles"
          subtitle="Importez votre liste de membres et visiteurs"
          icon={<IcoUsers />}
          type="participants"
          endpoint="/notifications/import/participants/"
          churchId={churchId}
          isPlatformAdmin={isPlatformAdmin}
          accentColor="#7c3aed"
        />
      </div>

      {/* Guide format */}
      <div style={{ ...s.card, background: 'var(--page-bg)' }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Conseils pour bien formater votre fichier
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {[
            { t: 'Première ligne = en-têtes', d: 'La ligne 1 doit contenir les noms de colonnes. Les données commencent à la ligne 2.' },
            { t: 'Formats de date acceptés', d: '2026-06-15 ou 15/06/2026 ou 15-06-2026. Les dates textuelles sont ignorées.' },
            { t: 'Format des heures', d: '09:30 ou 09h30 ou 9:30:00. Laisser vide si pas d\'heure.' },
            { t: 'Encodage CSV', d: 'Sauvegardez en UTF-8. Évitez les caractères spéciaux dans les noms de colonnes.' },
            { t: 'Genre (fidèles)', d: 'homme / femme / enfant — ou h / f / e. Par défaut : homme.' },
            { t: 'Statut (fidèles)', d: 'membre / nouveau / serviteur / invite. Par défaut : membre.' },
          ].map((tip, i) => (
            <div key={i} style={{ padding: '12px 14px', background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: 4 }}>{tip.t}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.55 }}>{tip.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
